import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  Grid,
  TextField,
  Typography,
  makeStyles,
  Select,
  MenuItem,
  InputLabel,
  FormControlLabel,
  Switch,
  CircularProgress,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import LaunchIcon from '@material-ui/icons/Launch';
import DescriptionIcon from '@material-ui/icons/Description';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { fetchDriveCompanies } from 'lib/Api/Drive';
import { DriveCompany } from 'lib/Model/Drive';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Warning } from '@material-ui/icons';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useDebounce } from 'hooks';
import { fetchQuoteHistory } from 'lib/Api/QuoteGenerator';
import { QuoteHistoryItem } from 'lib/Model/QuoteGenerator';
import { TermsHistoryTable } from 'views/TermsGenerator/TermsHistoryTable';
import { TermsGenerationDialog } from './TermsGenerationDialog';
import { getUserData } from 'lib/Helper/Auth';
import { fetchCompanyHierarchy } from 'lib/Api/TermsGenerator';
import { HierarchyCompany } from 'lib/Model/TermsGenerator';

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2),
  },
  alertMessage: {
    marginTop: theme.spacing(1),
  },
  icon: {
    verticalAlign: 'bottom',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  mainContainer: {
    padding: theme.spacing(2),
  },
  actionButton: {
    marginRight: theme.spacing(2),
  },
  searchBox: {
    padding: theme.spacing(1),
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
  },
  infoIcon: {
    fontSize: '1rem',
    marginRight: theme.spacing(1),
    verticalAlign: 'middle',
  },
  checkboxContainer: {
    marginBottom: theme.spacing(1),
  },
  suggestionsBox: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  suggestionItem: {
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  suggestionText: {
    marginRight: theme.spacing(2),
  },
  externalLinkIcon: {
    fontSize: '1rem',
  },
  folderSuggestionContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  radioGroup: {
    flexDirection: 'row',
    marginBottom: theme.spacing(2),
  },
  registrationTypeControl: {
    marginBottom: theme.spacing(2),
    minWidth: '100%',
  },
  fieldGroup: {
    marginBottom: theme.spacing(2),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  section: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  folderFoundMessage: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.success.main,
    marginTop: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
  folderNotFoundMessage: {
    display: 'flex',
    alignItems: 'center',
    color: 'black',
    marginTop: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
  workingFolderNotFoundMessage: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.info.dark,
    marginTop: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
  fieldWithButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  fieldGrow: {
    flexGrow: 1,
  },
  notAvailableButton: {
    whiteSpace: 'nowrap',
  },
  disabledField: {
    backgroundColor: theme.palette.action.disabledBackground,
  },
  consolidatedTermsSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  hierarchyCompanyList: {
    maxHeight: 300,
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  selectAllButton: {
    marginBottom: theme.spacing(1),
  },
  subsidiaryListItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  subsidiaryInfo: {
    flex: 1,
  },
  subsidiaryStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(2),
  },
  folderAvailableIcon: {
    color: theme.palette.success.main,
    fontSize: '1rem',
  },
  folderMissingIcon: {
    color: theme.palette.warning.main,
    fontSize: '1rem',
  },
  consolidatedInfoAlert: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

export const TermsGeneratorView: React.FC = () => {
  const classes = useStyles();
  const today = new Date();
  const user = getUserData();
  const permissions = user?.permissions ?? [];

  const hasSimPermission = permissions.includes('create_sim_agreements');
  const hasKonnektPermission = permissions.includes(
    'create_konnekt_terms_of_business',
  );
  const hasTalexioPermission = permissions.includes(
    'create_talexio_agreements',
  );
  const hasPayrollPermission = permissions.includes(
    'create_konnekt_payroll_agreements',
  );

  const hasAnyPermission =
    hasSimPermission ||
    hasKonnektPermission ||
    hasTalexioPermission ||
    hasPayrollPermission;

  const availableBrands = [
    ...(hasKonnektPermission ? [{ value: 'Konnekt', label: 'Konnekt' }] : []),
    ...(hasTalexioPermission ? [{ value: 'Talexio', label: 'Talexio' }] : []),
    ...(hasSimPermission ? [{ value: 'SIM', label: 'SIM' }] : []),
  ];

  const defaultBrand =
    availableBrands.length > 0
      ? (availableBrands[0].value as 'SIM' | 'Konnekt' | 'Talexio')
      : 'SIM';

  const [brand, setBrand] = useState<'SIM' | 'Konnekt' | 'Talexio'>(
    defaultBrand,
  );
  const [country, setCountry] = useState<'Malta' | 'Cyprus'>('Malta');
  const [termsType, setTermsType] = useState<'Standard' | 'Exclusive'>(
    'Standard',
  );
  const [companyName, setCompanyName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [registrationNumberDisabled, setRegistrationNumberDisabled] =
    useState(false);
  const [vatNumberDisabled, setVatNumberDisabled] = useState(false);
  const [address, setAddress] = useState('');
  const [addressFromZendesk, setAddressFromZendesk] = useState(false);
  const [signatory, setSignatory] = useState('');
  const [signatoryPosition, setSignatoryPosition] = useState('');
  const [dateOfIssue, setDateOfIssue] = useState<Date>(today);

  const [selectedCompanyFromDropdown, setSelectedCompanyFromDropdown] =
    useState<DriveCompany | null>(null);
  const [companies, setCompanies] = useState<DriveCompany[] | undefined>();
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [newlyCreatedFolderIds, setNewlyCreatedFolderIds] = useState<{
    parentFolderId?: string;
    workingFolderId?: string;
    agreementsFolderId?: string;
    proposalsFolderId?: string;
  } | null>(null);

  const [fileCreatedUrl, setFileCreatedUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const [dialogOpen, setDialogOpen] = useState(false);

  const [isConsolidatedTerms, setIsConsolidatedTerms] = useState(false);
  const [hierarchyCompanies, setHierarchyCompanies] = useState<
    HierarchyCompany[]
  >([]);
  const [selectedHierarchyCompanies, setSelectedHierarchyCompanies] = useState<
    string[]
  >([]);
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);

  const fetchCompanies = useCallback(() => {
    setLoadingCompanies(true);
    fetchDriveCompanies().then((d) => {
      setCompanies(d.data);
      setLoadingCompanies(false);
    });
  }, []);

  useEffect(() => {
    if (!loadingCompanies && companies === undefined) {
      fetchCompanies();
    }
  }, [fetchCompanies, loadingCompanies, companies]);

  useEffect(() => {
    if (selectedCompanyFromDropdown) {
      setCompanyName(selectedCompanyFromDropdown.company_title);
      setRegistrationNumber(selectedCompanyFromDropdown.CNumber || '');
      setVatNumber(selectedCompanyFromDropdown.VAT_number || '');

      if (selectedCompanyFromDropdown.address) {
        try {
          let addressObj;
          if (typeof selectedCompanyFromDropdown.address === 'string') {
            const processedAddressStr = selectedCompanyFromDropdown.address
              .replace(/'/g, '"')
              .replace(/None/g, 'null');
            addressObj = JSON.parse(processedAddressStr);
          } else {
            addressObj = selectedCompanyFromDropdown.address;
          }

          const addressParts = [];
          if (addressObj.line1) addressParts.push(addressObj.line1);
          if (addressObj.city) addressParts.push(addressObj.city);
          if (addressObj.state) addressParts.push(addressObj.state);
          if (addressObj.postal_code) addressParts.push(addressObj.postal_code);
          if (addressObj.country) addressParts.push(addressObj.country);

          const formattedAddress = addressParts.filter(Boolean).join(', ');
          setAddress(formattedAddress || '');
          setAddressFromZendesk(!!formattedAddress);
        } catch (error) {
          setAddress(
            typeof selectedCompanyFromDropdown.address === 'string'
              ? selectedCompanyFromDropdown.address
              : '',
          );
          setAddressFromZendesk(false);
        }
      } else {
        setAddress('');
        setAddressFromZendesk(false);
      }

      setRegistrationNumberDisabled(false);
      setVatNumberDisabled(false);

      setIsConsolidatedTerms(false);
      setHierarchyCompanies([]);
      setSelectedHierarchyCompanies([]);
    }
  }, [selectedCompanyFromDropdown]);

  useEffect(() => {
    const getHierarchy = async () => {
      if (brand === 'Konnekt' && selectedCompanyFromDropdown?.id) {
        setLoadingHierarchy(true);
        setHierarchyCompanies([]);
        try {
          const companies = await fetchCompanyHierarchy(
            selectedCompanyFromDropdown.id,
          );
          setHierarchyCompanies(companies);
          if (companies.length > 0) {
            setSelectedHierarchyCompanies([selectedCompanyFromDropdown.id]);
          }
        } finally {
          setLoadingHierarchy(false);
        }
      } else {
        setHierarchyCompanies([]);
        setIsConsolidatedTerms(false);
      }
    };

    getHierarchy();
  }, [brand, selectedCompanyFromDropdown]);

  const handleBrandChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setBrand(event.target.value as 'SIM' | 'Konnekt' | 'Talexio');
  };

  const handleCountryChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setCountry(event.target.value as 'Malta' | 'Cyprus');
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!companyName.trim()) {
      errors.companyName = 'Company name is required';
    }

    if (registrationNumberDisabled && vatNumberDisabled) {
      errors.identifiers =
        'At least one identifier (Registration or VAT) is required if marking both as N/A.';
    }

    if (brand === 'Talexio' && !selectedQuote) {
      errors.quote = 'Quote information is required for a Talexio agreement';
    }

    if ((brand === 'SIM' || brand === 'Talexio') && !effectiveDate) {
      errors.effectiveDate = 'Subscription start date is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = (): boolean => {
    if (!hasAnyPermission) {
      return false;
    }

    if (!companyName.trim()) {
      return false;
    }

    if (brand === 'Talexio' && !selectedQuote) {
      return false;
    }

    if (registrationNumberDisabled && vatNumberDisabled) {
      return false;
    }

    if ((brand === 'SIM' || brand === 'Talexio') && !effectiveDate) {
      return false;
    }

    return true;
  };

  const handleCreateFile = async () => {
    if (!validateForm()) {
      return;
    }

    setFileError(null);
    setDialogOpen(true);
  };

  const handleClear = () => {
    setBrand(defaultBrand);
    setCountry('Malta');
    setTermsType('Standard');
    setCompanyName('');
    setRegistrationNumber('');
    setVatNumber('');
    setIdNumber('');
    setRegistrationNumberDisabled(false);
    setVatNumberDisabled(false);
    setAddress('');
    setAddressFromZendesk(false);
    setSignatory('');
    setDateOfIssue(today);
    setSelectedCompanyFromDropdown(null);
    setFileCreatedUrl(null);
    setFileError(null);
    setValidationErrors({});
    setVatNumber('');
    setSelectedQuote(null);
    setQuoteSearchTerm('');

    setOrderFormNumber('');
    setOrderFormDate(null);
    setDealType('');
    setLicenseType('');
    setAgreementTerm('Thirty Six (36) months');
    setConfirmedBy('');
    setApprovedBy('');
    setEffectiveDate(null);
    setSubscriptionInvoiceDate(null);

    setIsConsolidatedTerms(false);
    setSelectedHierarchyCompanies([]);
    setNewlyCreatedFolderIds(null);
  };

  const getFolderStatusMessage = () => {
    if (!selectedCompanyFromDropdown) return null;

    const hasWorkingFolder =
      !!selectedCompanyFromDropdown.working_documents_folder_id;
    const hasParentFolder = !!selectedCompanyFromDropdown.parent_folder_id;

    if (!hasWorkingFolder && !hasParentFolder) {
      return (
        <Typography variant="caption" className={classes.folderNotFoundMessage}>
          <Warning fontSize="small" />A Google Drive folder does not exist for
          this client yet and will be automatically created with the document.
        </Typography>
      );
    }

    if (hasWorkingFolder) {
      return (
        <Typography variant="caption" className={classes.folderFoundMessage}>
          <CheckCircleIcon fontSize="small" />
          Drive folder found. Document will be saved in the working documents
          folder.
        </Typography>
      );
    } else {
      return (
        <Typography
          variant="caption"
          className={classes.workingFolderNotFoundMessage}
        >
          <Warning fontSize="small" />
          Drive Folder found, but could not find working documents folder. The
          document will be saved to the main company folder. If you save this
          file in the main company folder, please remember to move it to the
          working documents folder later.
        </Typography>
      );
    }
  };

  const handleOpenAssociationDialog = (
    termsId: number,
    companyName: string,
  ) => {};

  const handleFolderCreated = (folderIds: {
    parentFolderId?: string;
    workingFolderId?: string;
    agreementsFolderId?: string;
    proposalsFolderId?: string;
  }) => {
    setNewlyCreatedFolderIds(folderIds);
  };

  const effectiveCompany = selectedCompanyFromDropdown
    ? {
        ...selectedCompanyFromDropdown,
        parent_folder_id:
          selectedCompanyFromDropdown.parent_folder_id ||
          newlyCreatedFolderIds?.parentFolderId,
        working_documents_folder_id:
          selectedCompanyFromDropdown.working_documents_folder_id ||
          newlyCreatedFolderIds?.workingFolderId,
        agreements_folder_id:
          selectedCompanyFromDropdown.agreements_folder_id ||
          newlyCreatedFolderIds?.agreementsFolderId,
        proposals_folder_id:
          selectedCompanyFromDropdown.proposals_folder_id ||
          newlyCreatedFolderIds?.proposalsFolderId,
      }
    : null;

  const [selectedQuote, setSelectedQuote] = useState<QuoteHistoryItem | null>(
    null,
  );
  const [quoteSearchTerm, setQuoteSearchTerm] = useState('');
  const debouncedQuoteSearch = useDebounce(quoteSearchTerm, 400);
  const [quotes, setQuotes] = useState<QuoteHistoryItem[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);

  useEffect(() => {
    const fetchQuotes = async () => {
      if (brand === 'Talexio' && debouncedQuoteSearch.length > 1) {
        setLoadingQuotes(true);
        try {
          const response = await fetchQuoteHistory(1, 15, debouncedQuoteSearch);
          setQuotes(response.data);
        } catch (error) {
          setQuotes([]);
        } finally {
          setLoadingQuotes(false);
        }
      }
    };

    fetchQuotes();
  }, [brand, debouncedQuoteSearch]);

  useEffect(() => {
    if (brand !== 'Talexio') {
      setSelectedQuote(null);
      setQuoteSearchTerm('');
    }

    if (brand !== 'Konnekt') {
      setIsConsolidatedTerms(false);
      setSelectedHierarchyCompanies([]);
      setCountry('Malta');
    }
  }, [brand]);

  const handleQuoteChange = (
    event: React.ChangeEvent<{}>,
    newValue: QuoteHistoryItem | null,
  ) => {
    setSelectedQuote(newValue);
  };

  const formatQuoteOption = (option: QuoteHistoryItem) => {
    const date = new Date(option.created_on);
    const formattedDate = date.toLocaleDateString();
    return `Quote #${option.quote_id} - ${option.company_name} (${formattedDate})`;
  };

  const toggleRegistrationNumberDisabled = () => {
    setRegistrationNumberDisabled(!registrationNumberDisabled);
    if (!registrationNumberDisabled) {
      setRegistrationNumber('');
    }
  };

  const toggleVatNumberDisabled = () => {
    setVatNumberDisabled(!vatNumberDisabled);
    if (!vatNumberDisabled) {
      setVatNumber('');
    }
  };

  const [orderFormNumber, setOrderFormNumber] = useState<number | ''>('');
  const [orderFormDate, setOrderFormDate] = useState<Date | null>(null);
  const [dealType, setDealType] = useState('');
  const [licenseType, setLicenseType] = useState('');
  const [agreementTerm, setAgreementTerm] = useState('Thirty Six (36) months');
  const [confirmedBy, setConfirmedBy] = useState('');
  const [approvedBy, setApprovedBy] = useState('');
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(null);
  const [subscriptionInvoiceDate, setSubscriptionInvoiceDate] =
    useState<Date | null>(null);
  const [manuallySetInvoiceDate, setManuallySetInvoiceDate] = useState(false);

  useEffect(() => {
    if (effectiveDate && !manuallySetInvoiceDate) {
      const oneYearFromEffective = new Date(effectiveDate);
      oneYearFromEffective.setFullYear(oneYearFromEffective.getFullYear() + 1);
      setSubscriptionInvoiceDate(oneYearFromEffective);
    }
  }, [effectiveDate, manuallySetInvoiceDate]);

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleGenerationSuccess = () => {};

  const canCreateConsolidatedTerms = () => {
    return brand === 'Konnekt' && hierarchyCompanies.length > 1;
  };

  const handleSubsidiaryToggle = (companyId: string) => {
    if (companyId === selectedCompanyFromDropdown?.id) {
      return;
    }

    setSelectedHierarchyCompanies((prev) => {
      if (prev.includes(companyId)) {
        return prev.filter((id) => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const handleSelectAllSubsidiaries = () => {
    if (selectedHierarchyCompanies.length === hierarchyCompanies.length) {
      if (selectedCompanyFromDropdown?.id) {
        setSelectedHierarchyCompanies([selectedCompanyFromDropdown.id]);
      } else {
        setSelectedHierarchyCompanies([]);
      }
    } else {
      setSelectedHierarchyCompanies(hierarchyCompanies.map((c) => c.companyId));
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="Create Agreement"
            subheader="Generate Agreements for clients"
            action={
              <Button
                color="secondary"
                href="https://exp42.signrequest.com/"
                target="_blank"
                startIcon={<LaunchIcon />}
              >
                SignRequest
              </Button>
            }
          />
          <Divider />

          <CardContent className={classes.mainContainer}>
            {!hasAnyPermission ? (
              <Alert severity="info">
                You don't have permission to create any agreements.
              </Alert>
            ) : (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl
                      variant="outlined"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="brand-label">Brand</InputLabel>
                      <Select
                        labelId="brand-label"
                        value={brand}
                        onChange={handleBrandChange}
                        label="Brand"
                        disabled={!hasAnyPermission}
                      >
                        {availableBrands.map((brandOption) => (
                          <MenuItem
                            key={brandOption.value}
                            value={brandOption.value}
                          >
                            {brandOption.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {brand === 'Konnekt' && (
                    <Grid item xs={12} md={6}>
                      <FormControl
                        variant="outlined"
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel id="country-label">Country</InputLabel>
                        <Select
                          labelId="country-label"
                          value={country}
                          onChange={handleCountryChange}
                          label="Country"
                        >
                          <MenuItem value="Malta">Malta</MenuItem>
                          <MenuItem value="Cyprus">Cyprus</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>

                {/* COMPANY DETAILS SECTION */}
                <Paper variant="outlined" className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    1. Company Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Autocomplete
                        getOptionLabel={(c) => c.company_title}
                        options={companies ?? []}
                        loading={loadingCompanies}
                        value={selectedCompanyFromDropdown}
                        onChange={(e, newValue) => {
                          setSelectedCompanyFromDropdown(newValue);
                          if (newValue) {
                            setCompanyName(newValue.company_title);
                          } else {
                            setCompanyName('');
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="Company Name"
                            fullWidth
                            placeholder="Start typing to search for a company from Zendesk..."
                            InputLabelProps={{ shrink: true }}
                            error={!!validationErrors.companyName}
                            helperText={validationErrors.companyName}
                          />
                        )}
                      />
                      {selectedCompanyFromDropdown && getFolderStatusMessage()}
                    </Grid>

                    {/* Consolidated Terms Section */}
                    {brand === 'Konnekt' && selectedCompanyFromDropdown && (
                      <Grid item xs={12}>
                        {/* ... The entire consolidated terms section JSX remains here ... */}
                        {loadingHierarchy ? (
                          <Paper
                            elevation={0}
                            className={classes.consolidatedTermsSection}
                            style={{
                              padding: 16,
                              backgroundColor: '#f5f5f5',
                              textAlign: 'center',
                            }}
                          >
                            <CircularProgress size={24} />
                            <Typography
                              variant="caption"
                              display="block"
                              style={{ marginTop: 8 }}
                            >
                              Checking for related companies...
                            </Typography>
                          </Paper>
                        ) : (
                          canCreateConsolidatedTerms() && (
                            <Paper
                              elevation={0}
                              className={classes.consolidatedTermsSection}
                              style={{
                                padding: 16,
                                backgroundColor: '#f5f5f5',
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={isConsolidatedTerms}
                                    onChange={(e) => {
                                      setIsConsolidatedTerms(e.target.checked);
                                    }}
                                    color="primary"
                                  />
                                }
                                label={
                                  <Typography variant="subtitle1">
                                    Create Consolidated Terms of Business
                                  </Typography>
                                }
                              />
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                                style={{ marginLeft: 32 }}
                              >
                                Generate a single terms of business document
                                covering multiple companies in the same
                                organisation.
                              </Typography>

                              {isConsolidatedTerms && (
                                <>
                                  <Divider style={{ margin: '16px 0' }} />
                                  <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    style={{ marginBottom: 8 }}
                                  >
                                    Select companies to include in consolidated
                                    terms:
                                  </Typography>
                                  <Button
                                    size="small"
                                    onClick={handleSelectAllSubsidiaries}
                                    className={classes.selectAllButton}
                                    variant="outlined"
                                  >
                                    {selectedHierarchyCompanies.length ===
                                    hierarchyCompanies.length
                                      ? 'Deselect All'
                                      : 'Select All'}
                                  </Button>
                                  <Paper
                                    className={classes.hierarchyCompanyList}
                                  >
                                    <List dense>
                                      {hierarchyCompanies.map((company) => {
                                        const hasDriveFolder =
                                          !!company.parentFolderId;
                                        const isMainCompany =
                                          company.companyId ===
                                          selectedCompanyFromDropdown.id;

                                        return (
                                          <ListItem
                                            key={company.companyId}
                                            button
                                            onClick={() =>
                                              handleSubsidiaryToggle(
                                                company.companyId,
                                              )
                                            }
                                            disabled={isMainCompany}
                                          >
                                            <ListItemIcon>
                                              <Checkbox
                                                edge="start"
                                                checked={selectedHierarchyCompanies.includes(
                                                  company.companyId,
                                                )}
                                                tabIndex={-1}
                                                disableRipple
                                                disabled={isMainCompany}
                                              />
                                            </ListItemIcon>
                                            <div
                                              className={
                                                classes.subsidiaryListItem
                                              }
                                            >
                                              <div
                                                className={
                                                  classes.subsidiaryInfo
                                                }
                                              >
                                                <ListItemText
                                                  primary={company.companyName}
                                                  secondary={`C-Number: ${
                                                    company.CNumber || 'N/A'
                                                  }`}
                                                />
                                              </div>
                                              <div
                                                className={
                                                  classes.subsidiaryStatus
                                                }
                                              >
                                                {hasDriveFolder ? (
                                                  <>
                                                    <CheckCircleIcon
                                                      className={
                                                        classes.folderAvailableIcon
                                                      }
                                                    />
                                                    <Typography
                                                      variant="caption"
                                                      color="textSecondary"
                                                    >
                                                      Drive folder available
                                                    </Typography>
                                                  </>
                                                ) : (
                                                  <>
                                                    <ErrorOutlineIcon
                                                      className={
                                                        classes.folderMissingIcon
                                                      }
                                                    />
                                                    <Typography
                                                      variant="caption"
                                                      color="textSecondary"
                                                    >
                                                      Will create folder
                                                    </Typography>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          </ListItem>
                                        );
                                      })}
                                    </List>
                                  </Paper>
                                  {selectedHierarchyCompanies.length > 0 && (
                                    <Typography
                                      variant="caption"
                                      color="primary"
                                      style={{ marginTop: 8, display: 'block' }}
                                    >
                                      {selectedHierarchyCompanies.length}{' '}
                                      {selectedHierarchyCompanies.length === 1
                                        ? 'company'
                                        : 'companies'}{' '}
                                      selected
                                    </Typography>
                                  )}
                                </>
                              )}
                            </Paper>
                          )
                        )}
                      </Grid>
                    )}

                    {isConsolidatedTerms && (
                      <Grid item xs={12}>
                        <Alert
                          severity="info"
                          className={classes.consolidatedInfoAlert}
                        >
                          <Typography variant="body2" gutterBottom>
                            <strong>Note:</strong> When creating consolidated
                            terms, the Registration Number, VAT Number, and
                            Address fields below only apply to{' '}
                            <strong>{companyName}</strong>.
                          </Typography>
                          <Typography variant="body2">
                            If any information is missing for other selected
                            companies, it must be updated in Zendesk or manually
                            corrected in the generated document.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        className={classes.formControl}
                        error={!!validationErrors.registrationNumber}
                      >
                        <TextField
                          variant="outlined"
                          label="Registration Number"
                          value={registrationNumber}
                          onChange={(e) =>
                            setRegistrationNumber(e.target.value)
                          }
                          fullWidth
                          disabled={registrationNumberDisabled}
                          placeholder="Enter the company registration number"
                          InputLabelProps={{ shrink: true }}
                          error={!!validationErrors.registrationNumber}
                          helperText={validationErrors.registrationNumber}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={registrationNumberDisabled}
                              onChange={() =>
                                toggleRegistrationNumberDisabled()
                              }
                              color="primary"
                            />
                          }
                          label="Company has no Registration Number"
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        className={classes.formControl}
                        error={!!validationErrors.vatNumber}
                      >
                        <TextField
                          variant="outlined"
                          label="VAT Number"
                          value={vatNumber}
                          onChange={(e) => {
                            setVatNumber(e.target.value);
                          }}
                          fullWidth
                          disabled={vatNumberDisabled}
                          placeholder="Enter the company VAT number"
                          InputLabelProps={{ shrink: true }}
                          error={!!validationErrors.vatNumber}
                          helperText={validationErrors.vatNumber}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={vatNumberDisabled}
                              onChange={() => toggleVatNumberDisabled()}
                              color="primary"
                            />
                          }
                          label="Company has no VAT Number"
                        />
                      </FormControl>
                    </Grid>

                    {registrationNumberDisabled && vatNumberDisabled && (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          At least one identifier (Registration Number or VAT
                          Number) is required.
                        </Alert>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        className={classes.formControl}
                        error={!!validationErrors.address}
                      >
                        <TextField
                          variant="outlined"
                          label="Company Address"
                          value={address}
                          onChange={(e) => {
                            setAddress(e.target.value);
                            setAddressFromZendesk(false);
                          }}
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="Enter the company address"
                          InputLabelProps={{ shrink: true }}
                          error={!!validationErrors.address}
                          helperText={validationErrors.address}
                        />
                        {addressFromZendesk && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{ marginTop: '4px' }}
                          >
                            Fetched from Zendesk. May not be accurate.
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>

                {/* AGREEMENT DETAILS SECTION */}
                <Paper variant="outlined" className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    2. Agreement Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        className={classes.formControl}
                        error={!!validationErrors.signatory}
                      >
                        <TextField
                          variant="outlined"
                          label="Signatory"
                          value={signatory}
                          onChange={(e) => setSignatory(e.target.value)}
                          fullWidth
                          placeholder="Enter the name of the signatory"
                          InputLabelProps={{ shrink: true }}
                          error={!!validationErrors.signatory}
                          helperText={validationErrors.signatory}
                        />
                      </FormControl>
                    </Grid>

                    {brand !== 'Konnekt' && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <TextField
                            variant="outlined"
                            label="Signatory Position"
                            value={signatoryPosition}
                            onChange={(e) =>
                              setSignatoryPosition(e.target.value)
                            }
                            fullWidth
                            placeholder="Enter the position of the signatory (e.g. Director)"
                            InputLabelProps={{ shrink: true }}
                          />
                        </FormControl>
                      </Grid>
                    )}

                    {brand !== 'Konnekt' && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                              disableToolbar
                              variant="inline"
                              inputVariant="outlined"
                              format="dd/MM/yyyy"
                              label="Date of Agreement"
                              value={dateOfIssue}
                              onChange={(date) => {
                                if (date) {
                                  setDateOfIssue(date);
                                }
                              }}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          </MuiPickersUtilsProvider>
                        </FormControl>
                      </Grid>
                    )}

                    {(brand === 'Talexio' || brand === 'SIM') && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                              disableToolbar
                              variant="inline"
                              inputVariant="outlined"
                              format="dd/MM/yyyy"
                              label="Subscription Start Date"
                              value={effectiveDate}
                              required
                              onChange={setEffectiveDate}
                              fullWidth
                              placeholder="Select subscription start date"
                              InputLabelProps={{ shrink: true }}
                              error={!!validationErrors.effectiveDate}
                              helperText={validationErrors.effectiveDate}
                            />
                          </MuiPickersUtilsProvider>
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </Paper>

                {/* TALEXIO ORDER FORM SECTION */}
                {brand === 'Talexio' && (
                  <Paper variant="outlined" className={classes.section}>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      3. Talexio Order Form Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          className={classes.formControl}
                          error={!!validationErrors.quote}
                        >
                          <Autocomplete
                            options={quotes}
                            getOptionLabel={(option) =>
                              formatQuoteOption(option)
                            }
                            value={selectedQuote}
                            onChange={handleQuoteChange}
                            loading={loadingQuotes}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Proposal"
                                variant="outlined"
                                value={quoteSearchTerm}
                                onChange={(e) =>
                                  setQuoteSearchTerm(e.target.value)
                                }
                                placeholder="Search for a proposal by company name or proposal ID"
                                InputLabelProps={{ shrink: true }}
                                error={!!validationErrors.quote}
                                helperText={
                                  validationErrors.quote ||
                                  'Search and select a proposal to generate a Talexio agreement with the order form'
                                }
                                required
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loadingQuotes && (
                                        <CircularProgress
                                          color="inherit"
                                          size={20}
                                        />
                                      )}
                                      {params.InputProps?.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <TextField
                            variant="outlined"
                            label="Order Form Number"
                            value={orderFormNumber}
                            onChange={(e) => {
                              const value =
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value);
                              setOrderFormNumber(value);
                            }}
                            type="number"
                            fullWidth
                            placeholder="Enter order form number"
                            InputLabelProps={{ shrink: true }}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                              disableToolbar
                              variant="inline"
                              inputVariant="outlined"
                              format="dd/MM/yyyy"
                              label="Order Form Date"
                              value={orderFormDate}
                              onChange={setOrderFormDate}
                              fullWidth
                              placeholder="Select order form date"
                              InputLabelProps={{ shrink: true }}
                            />
                          </MuiPickersUtilsProvider>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <Autocomplete
                            options={[
                              'New Client Setup',
                              'Existing Client - Upgrade',
                              'New CSP Setup',
                              'Existing CSP Setup',
                            ]}
                            renderInput={(params) => (
                              <TextField
                                variant="outlined"
                                label="Deal Type"
                                {...params}
                                fullWidth
                                placeholder="Select a deal type"
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                            value={dealType}
                            onChange={(e, newValue) => {
                              setDealType(newValue || '');
                            }}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <Autocomplete
                            options={['Commercial', 'Freeware', 'Educational']}
                            renderInput={(params) => (
                              <TextField
                                variant="outlined"
                                label="License Type"
                                {...params}
                                fullWidth
                                placeholder="Select a license type"
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                            value={licenseType}
                            onChange={(e, newValue) => {
                              setLicenseType(newValue || '');
                            }}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <Autocomplete
                            options={[
                              'Twelve (12) months',
                              'Twenty Four (24) months',
                              'Thirty Six (36) months',
                            ]}
                            renderInput={(params) => (
                              <TextField
                                variant="outlined"
                                label="Agreement Term"
                                {...params}
                                fullWidth
                                placeholder="Select an agreement term"
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                            value={agreementTerm}
                            onChange={(e, newValue) => {
                              setAgreementTerm(
                                newValue || 'Thirty Six (36) months',
                              );
                            }}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                              disableToolbar
                              variant="inline"
                              inputVariant="outlined"
                              format="dd/MM/yyyy"
                              label="Subscription Invoice Date"
                              value={subscriptionInvoiceDate}
                              onChange={(date) => {
                                setSubscriptionInvoiceDate(date);
                                setManuallySetInvoiceDate(true);
                              }}
                              fullWidth
                              placeholder="Select subscription invoice date"
                              InputLabelProps={{ shrink: true }}
                            />
                          </MuiPickersUtilsProvider>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <TextField
                            variant="outlined"
                            label="Approved By"
                            value={approvedBy}
                            onChange={(e) => setApprovedBy(e.target.value)}
                            fullWidth
                            placeholder="Enter client's name and surname"
                            InputLabelProps={{ shrink: true }}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <TextField
                            variant="outlined"
                            label="Confirmed By"
                            value={confirmedBy}
                            onChange={(e) => setConfirmedBy(e.target.value)}
                            fullWidth
                            placeholder="Enter name of the internal staff member confirming the agreement"
                            InputLabelProps={{ shrink: true }}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                <Grid item xs={12} className={classes.buttonContainer}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateFile}
                    className={classes.actionButton}
                    startIcon={<DescriptionIcon />}
                    disabled={!isFormValid()}
                  >
                    Create Agreement
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClear}
                    disabled={!hasAnyPermission}
                  >
                    Clear
                  </Button>
                </Grid>

                {fileError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{fileError}</Alert>
                  </Grid>
                )}

                {fileCreatedUrl && (
                  <Grid item xs={12}>
                    <Alert
                      severity="success"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          href={fileCreatedUrl}
                          target="_blank"
                          startIcon={<LaunchIcon />}
                        >
                          Open
                        </Button>
                      }
                    >
                      Agreement created successfully!
                    </Alert>
                  </Grid>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="Agreement History"
            subheader="A history of all agreements generated through the system."
          />
          <CardContent>
            <TermsHistoryTable onLinkClick={handleOpenAssociationDialog} />
          </CardContent>
        </Card>
      </Grid>

      <TermsGenerationDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleGenerationSuccess}
        company={effectiveCompany}
        companyName={companyName}
        registrationNumber={registrationNumber}
        registrationType={registrationNumberDisabled ? 'vat' : 'registration'}
        address={address}
        signatory={signatory}
        signatoryPosition={signatoryPosition}
        dateOfIssue={dateOfIssue}
        brand={brand}
        country={brand === 'Konnekt' ? country : undefined}
        termsType={termsType}
        companyId={selectedCompanyFromDropdown?.id || ''}
        folderDestination="working"
        vatNumber={vatNumber}
        idNumber={idNumber}
        quoteId={selectedQuote?.quote_id}
        orderFormNumber={orderFormNumber || undefined}
        orderFormDate={
          orderFormDate ? orderFormDate.toISOString().split('T')[0] : undefined
        }
        dealType={dealType || undefined}
        licenseType={licenseType || undefined}
        agreementTerm={agreementTerm || undefined}
        confirmedBy={confirmedBy || undefined}
        approvedBy={approvedBy || undefined}
        effectiveDate={
          effectiveDate ? effectiveDate.toISOString().split('T')[0] : undefined
        }
        subscriptionInvoiceDate={
          subscriptionInvoiceDate
            ? subscriptionInvoiceDate.toISOString().split('T')[0]
            : undefined
        }
        isConsolidated={isConsolidatedTerms}
        selectedConsolidatedCompanies={selectedHierarchyCompanies}
        allHierarchyCompanies={hierarchyCompanies}
        onFolderCreated={handleFolderCreated}
      />
    </Grid>
  );
};
